import { Fragment } from "react";
import {
  Box,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import { usePagePrefs } from "@pepsico/pref-permissions";

import { useGetLocationByUuid } from "api/locations/locations";
import type { LocationResponse } from "api/model";

import SidePanelSectionHeader from "./SidePanelSectionHeader";

interface OverviewPanelProps {}

interface InfoRow {
  label: string;
  value: string;
}

const formatCoordinate = (value: number | null | undefined): string => {
  if (value == null) {
    return "—";
  }

  return value.toFixed(6);
};

const formatOptional = (value: string | null | undefined): string => {
  if (value == null || value === "") {
    return "—";
  }

  return value;
};

const formatGps = (
  latitude: number | null | undefined,
  longitude: number | null | undefined,
): string => {
  if (latitude == null && longitude == null) {
    return "—";
  }

  return `${formatCoordinate(latitude)}, ${formatCoordinate(longitude)}`;
};

const OverviewPanel: React.FC<OverviewPanelProps> = () => {
  const [prefs] = usePagePrefs();
  const locationUuid = (prefs.location as string | null | undefined) ?? "";

  const {
    data: location = null,
    isError,
    isPending,
  } = useGetLocationByUuid(locationUuid, {
    query: {
      enabled: locationUuid !== "",
      select: (res): LocationResponse | null =>
        res.status === 200 ? res.data : null,
    },
  });

  const locationName =
    location?.location_name != null && location.location_name !== ""
      ? location.location_name
      : isPending
        ? "Loading location…"
        : isError
          ? "Location unavailable"
          : locationUuid;

  const identityRows: InfoRow[] = location
    ? [
        {
          label: "Location Name",
          value: formatOptional(location.location_name),
        },
        {
          label: "Display Name",
          value: formatOptional(location.location_display_name),
        },
        {
          label: "Sector",
          value: formatOptional(
            location.sector != null ? String(location.sector) : null,
          ),
        },
      ]
    : [];

  const placeRows: InfoRow[] = location
    ? [
        { label: "City", value: formatOptional(location.city) },
        {
          label: "State",
          value: formatOptional(location.state_province),
        },
        {
          label: "Country",
          value: formatOptional(
            location.country != null ? String(location.country) : null,
          ),
        },
        {
          label: "GPS",
          value: formatGps(location.latitude, location.longitude),
        },
        {
          label: "Soil Type",
          value: formatOptional(location.soil_type),
        },
      ]
    : [];

  const infoGroups: { rows: InfoRow[]; title: string }[] = [
    { rows: identityRows, title: "IDENTITY" },
    { rows: placeRows, title: "PLACE" },
  ];

  return (
    <Stack spacing={1.5}>
      {isPending && location == null ? (
        <>
          <Box sx={{ alignItems: "center", display: "flex", gap: 1 }}>
            <CircularProgress aria-label="Loading location" size={18} />
            <Typography component="h2" variant="h6">
              Loading location…
            </Typography>
          </Box>
          <Divider />
        </>
      ) : (
        <>
          <SidePanelSectionHeader
            ariaLabel={
              locationName !== ""
                ? `Selected location ${locationName}`
                : "Selected location"
            }
            title={locationName}
          />
          {isError && location == null ? (
            <Typography color="error" variant="body2">
              Unable to load location details.
            </Typography>
          ) : (
            <Box
              sx={{
                columnGap: 2,
                display: "grid",
                gridTemplateColumns: "max-content 1fr",
                rowGap: 2,
              }}
            >
              {infoGroups.map(({ rows, title }) => (
                <Box
                  key={title}
                  aria-label={title}
                  role="group"
                  sx={{
                    display: "grid",
                    gridColumn: "1 / -1",
                    gridTemplateColumns: "subgrid",
                    rowGap: 1.5,
                  }}
                >
                  <Typography
                    component="h3"
                    sx={{ fontWeight: "bold", gridColumn: "1 / -1" }}
                    variant="subtitle2"
                  >
                    {title}
                  </Typography>
                  <Box
                    component="dl"
                    sx={{
                      display: "grid",
                      gridColumn: "1 / -1",
                      gridTemplateColumns: "subgrid",
                      m: 0,
                      rowGap: 1,
                    }}
                  >
                    {rows.map(({ label, value }) => (
                      <Fragment key={label}>
                        <Typography
                          color="text.secondary"
                          component="dt"
                          variant="caption"
                        >
                          {label}
                        </Typography>
                        <Typography
                          component="dd"
                          sx={{ m: 0 }}
                          variant="body2"
                        >
                          {value}
                        </Typography>
                      </Fragment>
                    ))}
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </>
      )}
    </Stack>
  );
};

export default OverviewPanel;
